import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', '6XL', 'Free', 'Free Size'];

const fmtDate = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 && p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0].slice(-2)}` : d;
};
const fmtAmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const TYPE_COLORS = {
  PO: 'bg-blue-100 text-blue-800 border-blue-300',
  PE: 'bg-teal-100 text-teal-800 border-teal-300',
  PR: 'bg-orange-100 text-orange-800 border-orange-300',
  SO: 'bg-violet-100 text-violet-800 border-violet-300',
  SE: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  SR: 'bg-red-100 text-red-800 border-red-300',
};

const TYPE_LABELS = {
  PO: 'Purchase Order', PE: 'Purchase Entry', PR: 'Purchase Return',
  SO: 'Sales Order', SE: 'Sales Entry', SR: 'Sales Return',
};

const STATUS_COLORS = {
  confirmed: 'bg-blue-100 text-blue-700', partial_received: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700', packed: 'bg-blue-100 text-blue-700',
  dispatched: 'bg-green-100 text-green-700', fulfilled: 'bg-green-100 text-green-700',
  partial_fulfilled: 'bg-amber-100 text-amber-700', pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
};

const EDIT_ROUTES = {
  PO: (id) => `/create-purchase-order?edit=${id}`,
  PE: (id) => `/create-purchase-entry?edit=${id}`,
  PR: (id) => `/create-purchase-return?edit=${id}`,
  SO: (id) => `/create-sales-order?edit=${id}`,
  SE: (id, data) => ({ pathname: '/create-sales-entry', state: { editSE: data } }),
  SR: (id) => `/create-sales-return?edit=${id}`,
};

const DELETE_ENDPOINTS = {
  PO: (id) => `/purchase-orders/${id}`,
  PE: (id) => `/purchase-entries/${id}`,
  PR: (id) => `/purchase-returns/${id}`,
  SO: (id) => `/sales-orders/${id}`,
  SE: (id) => `/sales-entries/${id}`,
  SR: (id) => `/sales-returns/${id}`,
};

export default function LedgerDetail({ user }) {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromType = searchParams.get('from') || '';
  const fromId = searchParams.get('fromId') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const loadData = useCallback(async () => {
    try {
      const res = await api.get(`/ledger/transaction/${type}/${id}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load transaction');
      goBack();
    } finally { setLoading(false); }
  }, [type, id]);

  useEffect(() => { loadData(); }, [loadData]);

  const goBack = () => {
    if (fromType && fromId) {
      navigate(`/ledger-report?type=${fromType}&id=${fromId}`);
    } else {
      navigate(-1);
    }
  };

  const handleEdit = () => {
    if (!data) return;
    const txType = data.type;
    if (txType === 'SE') {
      // SE edit needs full SE data from API
      const fetchAndNavigate = async () => {
        try {
          const res = await api.get(`/sales-entries/${id}`);
          navigate('/create-sales-entry', { state: { editSE: res.data } });
        } catch { toast.error('Failed to load SE for editing'); }
      };
      fetchAndNavigate();
    } else if (txType === 'SO') {
      navigate(`/create-sales-order`, { state: { editSO: { id } } });
    } else {
      // For PO/PE/PR/SR, navigate to respective create page with edit param
      const routes = { PO: '/create-purchase-order', PE: '/create-purchase-entry', PR: '/create-purchase-return', SR: '/create-sales-return' };
      navigate(routes[txType] || '/', { state: { editId: id } });
    }
  };

  const handleDelete = async () => {
    if (!data) return;
    setDeleting(true);
    try {
      const endpoint = DELETE_ENDPOINTS[data.type];
      if (!endpoint) { toast.error('Delete not supported for this type'); return; }
      await api.delete(endpoint(id));
      toast.success(`${data.number} deleted`);
      goBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); setDeleteConfirm(false); }
  };

  // Sort variants colour-wise (alphabetical), then by size within each colour
  const sortVariants = (variants) => {
    const sizeIdx = (s) => { const i = SIZE_ORDER.indexOf(s); return i === -1 ? 999 : i; };
    return [...variants].sort((a, b) => {
      const ca = (a.color || '').toLowerCase();
      const cb = (b.color || '').toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      return sizeIdx(a.size) - sizeIdx(b.size);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-6 space-y-4 animate-fadeIn" data-testid="ledger-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={goBack} data-testid="ledger-detail-back">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            Back to Ledger
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900 font-mono" data-testid="ledger-detail-number">{data.number}</h1>
              <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-bold border ${TYPE_COLORS[data.type] || ''}`}>
                {TYPE_LABELS[data.type] || data.type}
              </span>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[data.status] || 'bg-gray-50 text-gray-600'}`}>
                {(data.status || '').replace(/_/g, ' ')}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500 mt-0.5">
              <span>Date: <strong className="text-gray-700">{fmtDate(data.date)}</strong></span>
              {data.entity_name && <span>Entity: <strong className="text-gray-700">{data.entity_name}</strong></span>}
              {data.invoice_number && <span>Invoice: <strong className="text-gray-700">{data.invoice_number}</strong></span>}
              {data.transport_name && <span>Transport: <strong className="text-gray-700">{data.transport_name}</strong></span>}
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleEdit} data-testid="ledger-detail-edit">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Edit
            </Button>
            <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(true)} data-testid="ledger-detail-delete">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Delete
            </Button>
          </div>
        )}
      </div>

      {data.notes && (
        <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <strong>Notes:</strong> {data.notes}
        </div>
      )}

      {/* Items Table — Excel style */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-detail-table">
              <thead>
                <tr className="bg-gray-100 border-b-2 border-gray-300">
                  <th className="px-3 py-2.5 text-left font-bold text-gray-700 w-12">#</th>
                  <th className="px-3 py-2.5 text-left font-bold text-gray-700 w-16">Image</th>
                  <th className="px-3 py-2.5 text-left font-bold text-gray-700 min-w-[120px]">Design Code</th>
                  <th className="px-3 py-2.5 text-left font-bold text-gray-700 min-w-[100px]">Colour</th>
                  <th className="px-3 py-2.5 text-left font-bold text-gray-700 w-24">Size</th>
                  <th className="px-3 py-2.5 text-right font-bold text-gray-700 w-20">Qty</th>
                  <th className="px-3 py-2.5 text-right font-bold text-gray-700 w-24">Rate</th>
                  <th className="px-3 py-2.5 text-right font-bold text-gray-700 w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, itemIdx) => {
                  const variants = sortVariants(item.variants);
                  const hasImage = item.images && item.images.length > 0;
                  return variants.map((v, vIdx) => (
                    <tr key={`${itemIdx}-${vIdx}`}
                      className={`border-b border-gray-200 hover:bg-blue-50/30 ${vIdx === 0 && itemIdx > 0 ? 'border-t-2 border-t-gray-300' : ''}`}
                      data-testid={`detail-row-${itemIdx}-${vIdx}`}>
                      {vIdx === 0 && (
                        <>
                          <td className="px-3 py-2 text-gray-500 font-mono text-xs align-top" rowSpan={variants.length}>{itemIdx + 1}</td>
                          <td className="px-3 py-2 align-top" rowSpan={variants.length}>
                            {hasImage ? (
                              <img
                                src={`${API_BASE}${item.images[0]}`}
                                alt={item.design_code}
                                className="w-12 h-12 rounded object-cover cursor-pointer border border-gray-200 hover:border-blue-400 transition-colors"
                                onClick={() => setLightboxImg(`${API_BASE}${item.images[0]}`)}
                                data-testid={`detail-img-${itemIdx}`}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center text-gray-400">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono font-bold text-gray-900 align-top" rowSpan={variants.length}>
                            {item.design_code}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 text-gray-700">{v.color}</td>
                      <td className="px-3 py-2 text-gray-700">{v.size}</td>
                      <td className="px-3 py-2 text-right font-mono font-medium">{fmtAmt(v.qty)}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-600">{fmtAmt(v.rate)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtAmt(v.amount)}</td>
                    </tr>
                  ));
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 border-t-2 border-gray-400">
                  <td colSpan={5} className="px-3 py-3 font-bold text-gray-800 text-right">TOTAL</td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-gray-900 text-base">{fmtAmt(data.total_qty)}</td>
                  <td className="px-3 py-3 text-right text-gray-600"></td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-gray-900 text-base">{fmtAmt(data.total_amount)}</td>
                </tr>
                {data.freight_charges > 0 && (
                  <tr className="bg-gray-50 border-t border-gray-200">
                    <td colSpan={7} className="px-3 py-2 text-right text-sm text-gray-600">Freight Charges</td>
                    <td className="px-3 py-2 text-right font-mono font-medium">{fmtAmt(data.freight_charges)}</td>
                  </tr>
                )}
                {data.freight_charges > 0 && (
                  <tr className="bg-gray-100">
                    <td colSpan={7} className="px-3 py-2 text-right font-bold text-gray-800">GRAND TOTAL</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-gray-900 text-base">{fmtAmt(data.total_amount + data.freight_charges)}</td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={() => setLightboxImg(null)} data-testid="lightbox-overlay">
          <div className="max-w-3xl max-h-[90vh] p-2" onClick={e => e.stopPropagation()}>
            <img src={lightboxImg} alt="Product" className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain" />
            <button onClick={() => setLightboxImg(null)} className="absolute top-4 right-4 bg-white/90 rounded-full p-2 shadow hover:bg-white" data-testid="lightbox-close">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {data.number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this {TYPE_LABELS[data.type] || data.type} and reverse any stock changes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white border-2 border-red-700"
              data-testid="delete-confirm-btn">
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
